import { AxiosMutationReturn, UseMutationConfig } from './hooks.types';
import { useAxios } from './useAxios';
import { AxiosError, AxiosResponse } from 'axios';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'react-query';
import { keysToCamelCase } from 'utils/formatters';

/**
 * Axios mutation hook - for POST/PUT/PATCH/DELETE with or without authentication
 * @param config - Mutation configuration with onSuccess, onError callbacks and authentication flag
 */
export default function useAxiosMutation<T, E = AxiosError>(
  config: {
    onSuccess?: (response: { data: T; response: Partial<AxiosResponse> }) => void;
    onError?: (error: E, vars: UseMutationConfig) => void;
    isAuthenticationDisabled?: boolean;
  } = {}
): AxiosMutationReturn<T> {
  const { isAuthenticationDisabled, ...mutationConfig } = config;
  const { axiosRequest } = useAxios({ isAuthenticationDisabled });
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();

  const { mutateAsync, data, isError, error, isLoading, ...others } = useMutation<
    AxiosResponse<unknown>,
    E,
    UseMutationConfig
  >(axiosRequest, {
    onError: () => {
      enqueueSnackbar(t('genericErrors.somethingWentWrong'), {
        variant: 'error',
      });
    },
    ...mutationConfig,
    onSuccess: ({ data, ...response }) => {
      const { onSuccess } = mutationConfig;
      // Convert keys to camelCase
      if (typeof onSuccess === 'function') {
        onSuccess({ data: keysToCamelCase(data) as T, response });
      }
    },
  });

  return {
    mutateAsync: mutateAsync as unknown as (args: UseMutationConfig) => Promise<T>,
    data: keysToCamelCase(data?.data) as T | undefined,
    isError,
    error,
    isLoading,
    ...others,
  };
}
